import * as api from "./client";

// fct_intelligence_article_feed
export type IntelligenceArticleFeed = {
  tablePk: string;
  articleId: number;
  intelligenceRegionId: string;
  intelligenceCountryId: string | null;
  intelligenceContentType: string | null;
  articleSlug: string | null;
  articleTitle: string | null;
  articleDescription: string | null;
  articleSummary: string | null;
  locationName: string | null;
  publishedTs: string;
  intelligenceTopicId: string;
  topicName: string | null;
  companyId: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  numHighlightReads: number | null;
  numMustReads: number | null;
  numPopularReads: number | null;
  isHighlight: boolean;
  isMustRead: boolean;
  videoHtml: string | null;
  rowSyncedTs: string | null;
};

// dim_intelligence_article_detail
export type IntelligenceArticleDetail = {
  articleId: number;
  articleSlug: string | null;
  articleTitle: string | null;
  articleDescription: string | null;
  articleSummary: string | null;
  bodyText: string | null;
  intelligenceContentType: string | null;
  adCountryId: number | null;
  intelligenceCountryId: string | null;
  intelligenceRegionIds: unknown | null;
  countryIsoAlpha2: string | null;
  locationName: string | null;
  sourceUrl: string | null;
  publishedTs: string | null;
  lastModifiedTs: string | null;
  primaryTopicId: string | null;
  primaryTopicName: string | null;
  publishStatusName: string | null;
  isPrimaryAuthorHidden: boolean | null;
  videoHtml: string | null;
  companyId: string | null;
  linkedinCompanyId: number | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  companyDescription: string | null;
  companyProfileSlug: string | null;
  companyIsSubscriber: boolean | null;
  isAwardWinner: boolean | null;
  rowSyncedTs: string | null;
};

// dim_intelligence_topic
export type IntelligenceTopic = {
  intelligenceTopicId: string;
  topicName: string;
  topicSlug: string;
  adTopicId: number | null;
  rowSyncedTs: string;
};

// dim_intelligence_region
export type IntelligenceRegion = {
  intelligenceRegionId: string;
  parentRegionId: string | null;
  regionName: string;
  regionSlug: string | null;
  wpRegionSlug: string | null;
  sortOrder: number;
  rowSyncedTs: string;
};

// dim_intelligence_country
export type IntelligenceCountry = {
  countryIsoAlpha2: string;
  intelligenceRegionId: string;
  regionName: string;
  regionSlug: string;
  rowSyncedTs: string;
};

// fct_intelligence_event
export type IntelligenceEvent = {
  tablePk: string;
  eventId: number;
  intelligenceTopicId: string | null;
  intelligenceRegionId: string;
  intelligenceCountryId: string | null;
  eventTitle: string | null;
  eventTypeName: string | null;
  eventUrl: string | null;
  eventDt: string;
  eventStartDt: string | null;
  eventEndDt: string | null;
  countryName: string | null;
  topicName: string | null;
  companyId: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  rowSyncedTs: string | null;
};

// fct_intelligence_guide
export type IntelligenceGuide = {
  tablePk: string;
  guideId: number;
  intelligenceTopicId: string;
  guideTitle: string;
  guideSlug: string | null;
  intelligenceRegionIds: unknown | null;
  rowSyncedTs: string | null;
};

// fct_intelligence_article_author
export type IntelligenceArticleAuthor = {
  tablePk: string;
  articleId: number;
  contactId: string | null;
  authorTitle: string | null;
  authorForename: string | null;
  authorSurname: string | null;
  authorMiddleName: string | null;
  authorExternalProfileUrl: string | null;
  authorProfileSlug: string | null;
  authorImageUrl: string | null;
  isPrimaryAuthor: boolean | null;
  sortOrder: number | null;
  rowSyncedTs: string | null;
};

// fct_intelligence_article_topic
export type IntelligenceArticleTopic = {
  tablePk: string;
  articleId: number;
  intelligenceTopicId: string;
  intelligenceParentTopicId: string | null;
  isParent: boolean | null;
  isPrimaryTopic: boolean | null;
  topicName: string | null;
  topicSlug: string | null;
  rowSyncedTs: string | null;
};

// fct_intelligence_article_disclaimer
export type IntelligenceArticleDisclaimer = {
  tablePk: string;
  articleId: number;
  companyRulesId: number;
  disclaimerText: string | null;
  sortOrder: number | null;
  rowSyncedTs: string | null;
};

// fct_intelligence_article_state
export type IntelligenceArticleState = {
  tablePk: string;
  articleId: number;
  stateId: number;
  countryId: number;
  stateName: string;
  stateCode: string | null;
  rowSyncedTs: string | null;
};

// dim_intelligence_state
export type IntelligenceState = {
  stateId: number;
  countryId: number;
  stateName: string;
  stateCode: string | null;
  rowSyncedTs: string;
};

export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  requestId: string | null;
};

export type ArticleFeedQuery = {
  intelligenceRegionId?: string;
  intelligenceTopicId?: string;
  intelligenceContentType?: string;
  isHighlight?: boolean;
  isMustRead?: boolean;
  limit?: number;
  offset?: number;
};

export type ArticleDetailQuery = {
  intelligenceCountryId?: string;
  publishStatusName?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

export type TopicsQuery = { limit?: number; offset?: number };
export type RegionsQuery = { parentRegionId?: string; limit?: number; offset?: number };
export type CountriesQuery = { intelligenceRegionId?: string; limit?: number; offset?: number };
export type EventsQuery = {
  intelligenceRegionId?: string;
  intelligenceTopicId?: string;
  limit?: number;
  offset?: number;
};
export type GuidesQuery = { intelligenceTopicId?: string; limit?: number; offset?: number };
export type ArticleAuthorsQuery = { articleId?: number; limit?: number; offset?: number };
export type ArticleTopicsQuery = {
  articleId?: number;
  intelligenceTopicId?: string;
  limit?: number;
  offset?: number;
};
export type ArticleDisclaimersQuery = { articleId?: number; limit?: number; offset?: number };
export type ArticleStatesQuery = {
  articleId?: number;
  stateId?: number;
  limit?: number;
  offset?: number;
};
export type StatesQuery = { countryId?: number; limit?: number; offset?: number };

function buildArticleFeedQuery(params: ArticleFeedQuery): string {
  const searchParams = new URLSearchParams();
  if (params.intelligenceRegionId)
    searchParams.set("intelligenceRegionId", params.intelligenceRegionId);
  if (params.intelligenceTopicId)
    searchParams.set("intelligenceTopicId", params.intelligenceTopicId);
  if (params.intelligenceContentType)
    searchParams.set("intelligenceContentType", params.intelligenceContentType);
  if (params.isHighlight !== undefined)
    searchParams.set("isHighlight", params.isHighlight.toString());
  if (params.isMustRead !== undefined) searchParams.set("isMustRead", params.isMustRead.toString());
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildArticleDetailQuery(params: ArticleDetailQuery): string {
  const searchParams = new URLSearchParams();
  if (params.intelligenceCountryId)
    searchParams.set("intelligenceCountryId", params.intelligenceCountryId);
  if (params.publishStatusName) searchParams.set("publishStatusName", params.publishStatusName);
  if (params.search) searchParams.set("search", params.search);
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildTopicsQuery(params: TopicsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildRegionsQuery(params: RegionsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.parentRegionId) searchParams.set("parentRegionId", params.parentRegionId);
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildCountriesQuery(params: CountriesQuery): string {
  const searchParams = new URLSearchParams();
  if (params.intelligenceRegionId)
    searchParams.set("intelligenceRegionId", params.intelligenceRegionId);
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildEventsQuery(params: EventsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.intelligenceRegionId)
    searchParams.set("intelligenceRegionId", params.intelligenceRegionId);
  if (params.intelligenceTopicId)
    searchParams.set("intelligenceTopicId", params.intelligenceTopicId);
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildGuidesQuery(params: GuidesQuery): string {
  const searchParams = new URLSearchParams();
  if (params.intelligenceTopicId)
    searchParams.set("intelligenceTopicId", params.intelligenceTopicId);
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildArticleAuthorsQuery(params: ArticleAuthorsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.articleId !== undefined) searchParams.set("articleId", params.articleId.toString());
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildArticleTopicsQuery(params: ArticleTopicsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.articleId !== undefined) searchParams.set("articleId", params.articleId.toString());
  if (params.intelligenceTopicId)
    searchParams.set("intelligenceTopicId", params.intelligenceTopicId);
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildArticleDisclaimersQuery(params: ArticleDisclaimersQuery): string {
  const searchParams = new URLSearchParams();
  if (params.articleId !== undefined) searchParams.set("articleId", params.articleId.toString());
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildArticleStatesQuery(params: ArticleStatesQuery): string {
  const searchParams = new URLSearchParams();
  if (params.articleId !== undefined) searchParams.set("articleId", params.articleId.toString());
  if (params.stateId !== undefined) searchParams.set("stateId", params.stateId.toString());
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function buildStatesQuery(params: StatesQuery): string {
  const searchParams = new URLSearchParams();
  if (params.countryId !== undefined) searchParams.set("countryId", params.countryId.toString());
  if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined) searchParams.set("offset", params.offset.toString());
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export const intelligence = {
  articleFeed: {
    getAll: async (
      params: ArticleFeedQuery = {},
    ): Promise<PaginatedResponse<IntelligenceArticleFeed>> => {
      const queryString = buildArticleFeedQuery(params);
      return api.get<PaginatedResponse<IntelligenceArticleFeed>>(
        `/v1/data-marts/intelligence/article-feed${queryString}`,
      );
    },
  },
  articleDetail: {
    getAll: async (
      params: ArticleDetailQuery = {},
    ): Promise<PaginatedResponse<IntelligenceArticleDetail>> => {
      const queryString = buildArticleDetailQuery(params);
      return api.get<PaginatedResponse<IntelligenceArticleDetail>>(
        `/v1/data-marts/intelligence/article-detail${queryString}`,
      );
    },
  },
  topics: {
    getAll: async (params: TopicsQuery = {}): Promise<PaginatedResponse<IntelligenceTopic>> => {
      const queryString = buildTopicsQuery(params);
      return api.get<PaginatedResponse<IntelligenceTopic>>(
        `/v1/data-marts/intelligence/topics${queryString}`,
      );
    },
  },
  regions: {
    getAll: async (params: RegionsQuery = {}): Promise<PaginatedResponse<IntelligenceRegion>> => {
      const queryString = buildRegionsQuery(params);
      return api.get<PaginatedResponse<IntelligenceRegion>>(
        `/v1/data-marts/intelligence/regions${queryString}`,
      );
    },
  },
  countries: {
    getAll: async (
      params: CountriesQuery = {},
    ): Promise<PaginatedResponse<IntelligenceCountry>> => {
      const queryString = buildCountriesQuery(params);
      return api.get<PaginatedResponse<IntelligenceCountry>>(
        `/v1/data-marts/intelligence/countries${queryString}`,
      );
    },
  },
  events: {
    getAll: async (params: EventsQuery = {}): Promise<PaginatedResponse<IntelligenceEvent>> => {
      const queryString = buildEventsQuery(params);
      return api.get<PaginatedResponse<IntelligenceEvent>>(
        `/v1/data-marts/intelligence/events${queryString}`,
      );
    },
  },
  guides: {
    getAll: async (params: GuidesQuery = {}): Promise<PaginatedResponse<IntelligenceGuide>> => {
      const queryString = buildGuidesQuery(params);
      return api.get<PaginatedResponse<IntelligenceGuide>>(
        `/v1/data-marts/intelligence/guides${queryString}`,
      );
    },
  },
  articleAuthors: {
    getAll: async (
      params: ArticleAuthorsQuery = {},
    ): Promise<PaginatedResponse<IntelligenceArticleAuthor>> => {
      const queryString = buildArticleAuthorsQuery(params);
      return api.get<PaginatedResponse<IntelligenceArticleAuthor>>(
        `/v1/data-marts/intelligence/article-authors${queryString}`,
      );
    },
  },
  articleTopics: {
    getAll: async (
      params: ArticleTopicsQuery = {},
    ): Promise<PaginatedResponse<IntelligenceArticleTopic>> => {
      const queryString = buildArticleTopicsQuery(params);
      return api.get<PaginatedResponse<IntelligenceArticleTopic>>(
        `/v1/data-marts/intelligence/article-topics${queryString}`,
      );
    },
  },
  articleDisclaimers: {
    getAll: async (
      params: ArticleDisclaimersQuery = {},
    ): Promise<PaginatedResponse<IntelligenceArticleDisclaimer>> => {
      const queryString = buildArticleDisclaimersQuery(params);
      return api.get<PaginatedResponse<IntelligenceArticleDisclaimer>>(
        `/v1/data-marts/intelligence/article-disclaimers${queryString}`,
      );
    },
  },
  articleStates: {
    getAll: async (
      params: ArticleStatesQuery = {},
    ): Promise<PaginatedResponse<IntelligenceArticleState>> => {
      const queryString = buildArticleStatesQuery(params);
      return api.get<PaginatedResponse<IntelligenceArticleState>>(
        `/v1/data-marts/intelligence/article-states${queryString}`,
      );
    },
  },
  states: {
    getAll: async (params: StatesQuery = {}): Promise<PaginatedResponse<IntelligenceState>> => {
      const queryString = buildStatesQuery(params);
      return api.get<PaginatedResponse<IntelligenceState>>(
        `/v1/data-marts/intelligence/states${queryString}`,
      );
    },
  },
};
